/* ROMANTICO - <rom-turntable>
   One custom element, three interchangeable engines behind one small surface:

     setPlaying(bool)   the deck spins or rests
     setProgress(0..1)  the transport reflects the position
     setLabelArt(url)   the centre label shows cover art

   files    dependency free HTML5 Audio over the rendered Track rows
   spotify  Spotify's IFrame API drives the deck, the embed stays visible
   visual   no engine at all, the disc turns in CSS

   Nothing here loads on page load. The Spotify API is fetched on intersection,
   the same deferral rom-map.js uses for Leaflet, and audio always waits for a
   click. requestAnimationFrame is used in exactly one bounded place: one frame
   per pointermove for the sheen, mouse only. */
(function () {
  if (window.customElements && customElements.get('rom-turntable')) return;

  var SHEEN_MAX = 6;
  var SPOTIFY_API = 'https://open.spotify.com/embed/iframe-api/v1';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function clockTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var total = Math.floor(seconds);
    var minutes = Math.floor(total / 60);
    var rest = total % 60;
    return minutes + ':' + (rest < 10 ? '0' + rest : rest);
  }

  /* An open.spotify.com link in any shape becomes a spotify:type:id URI, which
     is what createController expects. Returns '' when the URL is not one. */
  function spotifyUri(url) {
    if (!url) return '';
    var match = String(url).match(
      /open\.spotify\.com\/(?:embed\/)?(?:intl-[a-z-]+\/)?(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/
    );
    return match ? 'spotify:' + match[1] + ':' + match[2] : '';
  }

  /* window.onSpotifyIframeApiReady is a single global, so several turntables on
     one page have to share it. First caller installs the hook and the script
     tag; everyone else waits on the same promise. */
  var spotifyReady = null;
  function loadSpotifyApi() {
    if (spotifyReady) return spotifyReady;
    spotifyReady = new Promise(function (resolve, reject) {
      if (window.SpotifyIframeApi) return resolve(window.SpotifyIframeApi);
      window.onSpotifyIframeApiReady = function (api) {
        window.SpotifyIframeApi = api;
        resolve(api);
      };
      var script = document.querySelector('script[data-rom-spotify-api]');
      if (!script) {
        script = document.createElement('script');
        script.src = SPOTIFY_API;
        script.async = true;
        script.setAttribute('data-rom-spotify-api', '');
        script.addEventListener('error', reject);
        document.head.appendChild(script);
      }
    });
    return spotifyReady;
  }

  class RomTurntable extends HTMLElement {
    connectedCallback() {
      /* Shopify re-renders the whole section on every editor change. */
      if (this._init) return;
      this._init = true;

      this.source = this.dataset.source || 'visual';
      this.deck = this.querySelector('.rom-tt__deck');
      this.record = this.querySelector('.rom-tt__record');
      this.label = this.querySelector('.rom-tt__label');
      this.labelArt = this.querySelector('.rom-tt__label-art');
      this.sheen = this.querySelector('.rom-tt__sheen');

      this.sheenFrame = 0;
      this.pointerX = 0;
      this.pointerY = 0;

      this.bindSheen();
      this.bindPageEvents();

      if (this.source === 'files') this.initFiles();
      else if (this.source === 'spotify') this.initSpotify();
    }

    disconnectedCallback() {
      this.teardownFiles();
      this.teardownSpotify();

      if (this.deck && this._onPointerMove) {
        this.deck.removeEventListener('pointermove', this._onPointerMove);
        this.deck.removeEventListener('pointerleave', this._onPointerLeave);
      }
      if (this._onPageHide) window.removeEventListener('pagehide', this._onPageHide);
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.sheenFrame) cancelAnimationFrame(this.sheenFrame);
      this.sheenFrame = 0;
      this._init = false;
    }

    /* ---- shared surface -------------------------------------------------- */

    setPlaying(on) {
      this.classList.toggle('is-playing', !!on);
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        this.playBtn.setAttribute('aria-label', on ? 'Pause' : 'Play');
      }
    }

    setProgress(ratio) {
      if (!isFinite(ratio) || ratio < 0) ratio = 0;
      if (ratio > 1) ratio = 1;
      this.style.setProperty('--rom-tt-progress', (ratio * 100).toFixed(2) + '%');
      if (this.seek) this.seek.value = String(Math.round(ratio * 1000));
    }

    setLabelArt(url) {
      if (!this.labelArt) return;
      if (url) {
        this.labelArt.src = url;
        this.labelArt.removeAttribute('hidden');
        if (this.label) this.label.classList.add('has-art');
      } else {
        this.labelArt.removeAttribute('src');
        this.labelArt.setAttribute('hidden', '');
        if (this.label) this.label.classList.remove('has-art');
      }
    }

    bindPageEvents() {
      var deck = this;
      this._onPageHide = function () {
        deck.haltAudio();
      };
      this._onVisibility = function () {
        if (document.hidden) deck.haltAudio();
      };
      window.addEventListener('pagehide', this._onPageHide);
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    /* Only the files engine owns an audio element we can stop. The Spotify
       embed is Spotify's to control, and the visual deck is silent. */
    haltAudio() {
      if (this.audio && !this.audio.paused) this.audio.pause();
    }

    /* ---- sheen ----------------------------------------------------------- */

    bindSheen() {
      if (!this.sheen || !this.deck) return;
      if (this.dataset.finish === 'press') return;
      if (prefersReducedMotion()) return;

      var deck = this;
      this._onPointerMove = function (event) {
        /* Mouse only. On touch the highlight would jump under the finger. */
        if (event.pointerType && event.pointerType !== 'mouse') return;
        deck.pointerX = event.clientX;
        deck.pointerY = event.clientY;
        if (deck.sheenFrame) return;
        deck.sheenFrame = requestAnimationFrame(function () {
          deck.sheenFrame = 0;
          deck.moveSheen();
        });
      };
      this._onPointerLeave = function () {
        if (deck.sheenFrame) {
          cancelAnimationFrame(deck.sheenFrame);
          deck.sheenFrame = 0;
        }
        deck.sheen.style.transform = '';
      };

      this.deck.addEventListener('pointermove', this._onPointerMove);
      this.deck.addEventListener('pointerleave', this._onPointerLeave);
    }

    moveSheen() {
      var rect = this.deck.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var x = ((this.pointerX - rect.left) / rect.width - 0.5) * 2;
      var y = ((this.pointerY - rect.top) / rect.height - 0.5) * 2;
      x = Math.max(-1, Math.min(1, x));
      y = Math.max(-1, Math.min(1, y));
      /* The base rule centres the sheen with a translate, so keep it. */
      this.sheen.style.transform =
        'translate(-50%, -50%) translate3d(' +
        (x * SHEEN_MAX).toFixed(2) +
        'px, ' +
        (y * SHEEN_MAX).toFixed(2) +
        'px, 0)';
    }

    /* ======================================================================
       ENGINE: files
       Ported from the /pages/listen player. Tracks are read from the rendered
       DOM, so Liquid owns the data and this owns the behaviour.
       ====================================================================== */

    initFiles() {
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('.rom-tt__track'));
      if (!this.buttons.length) return;

      this.audio = new Audio();
      this.audio.preload = 'none';
      this.loop = this.dataset.loop === 'true';
      this.index = -1;

      this.tracks = this.buttons.map(function (btn) {
        return {
          src: btn.dataset.src || '',
          title: btn.dataset.title || 'Untitled',
          artist: btn.dataset.artist || '',
          art: btn.dataset.art || '',
          row: btn.closest('.rom-tt__row'),
          timeEl: btn.querySelector('.rom-tt__track-time'),
        };
      });

      this.playBtn = this.querySelector('.rom-tt__btn--play');
      this.prevBtn = this.querySelector('[data-rom-tt="prev"]');
      this.nextBtn = this.querySelector('[data-rom-tt="next"]');
      this.seek = this.querySelector('.rom-tt__seek');
      this.nowTitle = this.querySelector('.rom-tt__now-title');
      this.nowArtist = this.querySelector('.rom-tt__now-artist');
      this.timeCurrent = this.querySelector('.rom-tt__time-current');
      this.timeTotal = this.querySelector('.rom-tt__time-total');

      this.bindFiles();
      this.prefetchDurations();
      /* Cue the first track, but never start it: audio is opt in. */
      this.loadTrack(0, false);
    }

    bindFiles() {
      var deck = this;
      this._onPlayClick = function () { deck.toggle(); };
      this._onPrev = function () { deck.prev(); };
      this._onNext = function () { deck.next(); };
      this._onSeekInput = function (event) { deck.seekTo(event.target.value); };
      this._onTime = function () { deck.tick(); };
      this._onMeta = function () { deck.onMeta(); };
      this._onEnded = function () { deck.next(); };
      this._onPlay = function () { deck.setPlaying(true); };
      this._onPause = function () { deck.setPlaying(false); };
      this._onError = function () { deck.onError(); };
      this._onRecordClick = function () { deck.toggle(); };
      this._onBlockSelect = function (event) { deck.onBlockSelect(event); };

      if (this.playBtn) this.playBtn.addEventListener('click', this._onPlayClick);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      if (this.seek) this.seek.addEventListener('input', this._onSeekInput);
      if (this.record) this.record.addEventListener('click', this._onRecordClick);

      this.audio.addEventListener('timeupdate', this._onTime);
      this.audio.addEventListener('loadedmetadata', this._onMeta);
      this.audio.addEventListener('ended', this._onEnded);
      this.audio.addEventListener('play', this._onPlay);
      this.audio.addEventListener('pause', this._onPause);
      this.audio.addEventListener('error', this._onError);

      this.buttons.forEach(function (btn, i) {
        btn._romHandler = function () { deck.select(i); };
        btn.addEventListener('click', btn._romHandler);
      });

      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    teardownFiles() {
      if (!this.audio) return;
      if (this.playBtn && this._onPlayClick) this.playBtn.removeEventListener('click', this._onPlayClick);
      if (this.prevBtn && this._onPrev) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn && this._onNext) this.nextBtn.removeEventListener('click', this._onNext);
      if (this.seek && this._onSeekInput) this.seek.removeEventListener('input', this._onSeekInput);
      if (this.record && this._onRecordClick) this.record.removeEventListener('click', this._onRecordClick);

      this.audio.removeEventListener('timeupdate', this._onTime);
      this.audio.removeEventListener('loadedmetadata', this._onMeta);
      this.audio.removeEventListener('ended', this._onEnded);
      this.audio.removeEventListener('play', this._onPlay);
      this.audio.removeEventListener('pause', this._onPause);
      this.audio.removeEventListener('error', this._onError);

      (this.buttons || []).forEach(function (btn) {
        if (btn._romHandler) btn.removeEventListener('click', btn._romHandler);
      });
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);

      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio = null;
    }

    loadTrack(index, autoplay) {
      var track = this.tracks[index];
      if (!track) return;
      this.index = index;

      this.tracks.forEach(function (item, i) {
        var active = i === index;
        if (item.row) item.row.classList.toggle('is-active', active);
      });
      this.buttons.forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      if (this.nowTitle) this.nowTitle.textContent = track.title;
      if (this.nowArtist) {
        this.nowArtist.textContent = track.src
          ? track.artist
          : 'Add an audio file URL for this track in the theme editor';
      }

      this.setLabelArt(track.art);
      this.setProgress(0);
      if (this.timeCurrent) this.timeCurrent.textContent = '0:00';

      if (track.src) {
        this.audio.src = track.src;
        if (autoplay) this.play();
      } else {
        this.audio.removeAttribute('src');
        this.setPlaying(false);
      }
    }

    select(index) {
      if (index === this.index) this.toggle();
      else this.loadTrack(index, true);
    }

    play() {
      var track = this.tracks[this.index];
      if (!track || !track.src) return;
      var played = this.audio.play();
      if (played && played.catch) {
        var deck = this;
        played.catch(function () { deck.setPlaying(false); });
      }
    }

    toggle() {
      if (!this.audio) return;
      if (this.audio.paused) this.play();
      else this.audio.pause();
    }

    next() {
      var i = this.index + 1;
      if (i >= this.tracks.length) {
        if (!this.loop) {
          this.setPlaying(false);
          this.audio.currentTime = 0;
          return;
        }
        i = 0;
      }
      this.loadTrack(i, true);
    }

    prev() {
      /* Past three seconds, "previous" means "start this one again". */
      if (this.audio.currentTime > 3) {
        this.audio.currentTime = 0;
        return;
      }
      var i = this.index - 1;
      if (i < 0) i = this.loop ? this.tracks.length - 1 : 0;
      this.loadTrack(i, true);
    }

    seekTo(value) {
      var duration = this.audio.duration;
      if (!isFinite(duration) || duration <= 0) return;
      this.audio.currentTime = (value / 1000) * duration;
    }

    tick() {
      var duration = this.audio.duration;
      var current = this.audio.currentTime;
      if (this.timeCurrent) this.timeCurrent.textContent = clockTime(current);
      if (isFinite(duration) && duration > 0) this.setProgress(current / duration);
    }

    onMeta() {
      var duration = this.audio.duration;
      if (!isFinite(duration)) return;
      if (this.timeTotal) this.timeTotal.textContent = clockTime(duration);
      var track = this.tracks[this.index];
      if (track && track.timeEl && track.timeEl.dataset.fixed !== 'true') {
        track.timeEl.textContent = clockTime(duration);
      }
    }

    onError() {
      if (this.nowArtist) {
        this.nowArtist.textContent = 'Could not load this audio file, check the URL';
      }
      this.setPlaying(false);
    }

    prefetchDurations() {
      var deck = this;
      this.tracks.forEach(function (track) {
        if (!track.src || !track.timeEl) return;
        if (track.timeEl.dataset.fixed === 'true') return;
        var probe = new Audio();
        probe.preload = 'metadata';
        probe.src = track.src;
        probe.addEventListener('loadedmetadata', function () {
          if (isFinite(probe.duration)) track.timeEl.textContent = clockTime(probe.duration);
        });
        probe.addEventListener('error', function () {});
      });
    }

    /* Theme editor: jump to whichever Track block the merchant just clicked. */
    onBlockSelect(event) {
      var btn = this.querySelector('[data-block-id="' + event.detail.blockId + '"]');
      if (!btn) return;
      var i = this.buttons.indexOf(btn);
      if (i > -1) this.loadTrack(i, false);
    }

    /* ======================================================================
       ENGINE: spotify
       The embed stays visible and in charge. We only mirror its state onto
       the deck, and borrow the cover art for the label.
       ====================================================================== */

    initSpotify() {
      this.embedHost = this.querySelector('[data-rom-tt-embed]');
      this.spotifyUrl = this.dataset.spotifyUrl || '';
      this.uri = spotifyUri(this.dataset.embedSrc || this.spotifyUrl);
      if (!this.embedHost || !this.uri) return;

      var deck = this;
      if (!('IntersectionObserver' in window)) {
        this.mountSpotify();
        return;
      }
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            deck.observer.disconnect();
            deck.observer = null;
            deck.mountSpotify();
          });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(this);
    }

    /* Nothing here runs until the deck is near the viewport, the cover art
       lookup included: a section sitting at the bottom of a long page must not
       reach out to Spotify at all. */
    mountSpotify() {
      var deck = this;
      this.fetchCoverArt();
      loadSpotifyApi()
        .then(function (api) {
          if (!deck.isConnected || !deck.embedHost) return;
          api.createController(
            deck.embedHost,
            { uri: deck.uri, width: '100%', height: deck.embedHost.style.minHeight || '352' },
            function (controller) {
              deck.controller = controller;
              controller.addListener('playback_update', function (event) {
                var data = (event && event.data) || {};
                deck.setPlaying(!data.isPaused);
                if (data.duration > 0) deck.setProgress(data.position / data.duration);
              });
            }
          );
        })
        .catch(function () {
          /* Spotify unreachable or blocked: the deck stays a silent visual,
             which is exactly what the noscript iframe already covers. */
        });
    }

    teardownSpotify() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.controller && typeof this.controller.destroy === 'function') {
        this.controller.destroy();
      }
      this.controller = null;
    }

    /* Public oEmbed endpoint, no credentials and no SDK. Same source the Mac
       app uses for artwork. Silent on failure: the typographic label stands on
       its own. Skipped when the merchant picked a label image by hand. */
    fetchCoverArt() {
      if (!this.labelArt || this.labelArt.getAttribute('src')) return;
      if (!this.spotifyUrl) return;
      var deck = this;
      fetch('https://open.spotify.com/oembed?url=' + encodeURIComponent(this.spotifyUrl))
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          if (data && data.thumbnail_url && deck.isConnected) deck.setLabelArt(data.thumbnail_url);
        })
        .catch(function () {});
    }
  }

  customElements.define('rom-turntable', RomTurntable);
})();
